/**
 * Priority Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { priorityService } from './priority.service'
import { tokenStorage } from '@/lib/api'
import type {
  CreatePriorityData,
  UpdatePriorityData,
} from '../../domain/priority/types'

const QUERY_KEYS = {
  priorities: 'priorities',
  prioritiesWithCount: 'priorities-with-count',
  priority: (id: string) => ['priority', id],
  defaultPriority: 'default-priority',
}

/**
 * Hook for listing active priorities
 */
export function usePriorities() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: [QUERY_KEYS.priorities],
    queryFn: () => priorityService.list(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: hasToken,
    select: (data) => data.priorities,
  })
}

/**
 * Hook for listing all priorities including inactive (for admin)
 */
export function useAllPriorities() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: [QUERY_KEYS.prioritiesWithCount],
    queryFn: () => priorityService.list({ includeInactive: true, withCount: true }),
    enabled: hasToken,
    select: (data) => data.priorities,
  })
}

/**
 * Hook for getting default priority
 */
export function useDefaultPriority() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: [QUERY_KEYS.defaultPriority],
    queryFn: () => priorityService.getDefault(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook for getting a single priority
 */
export function usePriority(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.priority(id),
    queryFn: () => priorityService.getById(id),
    enabled: !!id,
  })
}

/**
 * Hook for creating a priority
 */
export function useCreatePriority() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePriorityData) => priorityService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.priorities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.prioritiesWithCount] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.defaultPriority] })
    },
  })
}

/**
 * Hook for updating a priority
 */
export function useUpdatePriority() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePriorityData }) =>
      priorityService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.priorities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.prioritiesWithCount] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.priority(variables.id) })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.defaultPriority] })
    },
  })
}

/**
 * Hook for deleting a priority
 */
export function useDeletePriority() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => priorityService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.priorities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.prioritiesWithCount] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.defaultPriority] })
    },
  })
}

/**
 * Hook for setting a priority as default
 */
export function useSetDefaultPriority() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => priorityService.setDefault(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.priorities] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.prioritiesWithCount] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.priority(id) })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.defaultPriority] })
    },
  })
}

export { priorityService }
